import React from 'react';
import {
  HiOutlineUserCircle,
  HiOutlineChartBar,
  HiOutlineSave,
  HiOutlineRefresh,
  HiOutlineLink,
  HiOutlineViewGridAdd,
  HiOutlineColorSwatch,
  HiOutlineUserGroup,
  HiOutlineAdjustments,
  HiOutlineClipboardCheck
} from 'react-icons/hi';

/**
 * 설정 메뉴 구성 파일
 *
 * 구조:
 * - SETTING_GROUPS: 그룹 정의 (key, title, order)
 * - SETTING_MENUS: 개별 메뉴 정의 (key, label, icon, groupKey, description)
 * - unifiedSettingsMenuConfig: 통합 메뉴 배열 (자동 생성)
 *
 * @typedef {Object} SettingGroup
 * @property {string} key - 그룹 고유 키
 * @property {string} title - 그룹 제목
 * @property {number} order - 그룹 정렬 순서
 *
 * @typedef {Object} SettingMenu
 * @property {string} key - 메뉴 고유 키
 * @property {string} label - 메뉴 레이블
 * @property {ReactElement} icon - 메뉴 아이콘
 * @property {string} groupKey - 소속 그룹 키
 * @property {string} description - 설명 (툴팁)
 */

// ==================== 그룹 정의 ====================
export const SETTING_GROUPS = {
  model: { key: 'model', title: '모델 설정', order: 1 },
  user: { key: 'user', title: '사용자 설정', order: 2 },
  environment: { key: 'environment', title: '환경 설정', order: 3 }
};

// ==================== 메뉴 정의 ====================
export const SETTING_MENUS = {
  // 모델 설정
  saveOption: {
    key: 'saveOption',
    label: '저장옵션',
    icon: <HiOutlineSave />,
    groupKey: 'model',
    description: '모델 저장 관련 설정'
  },
  physicalConversion: {
    key: 'physicalConversion',
    label: '물리전환',
    icon: <HiOutlineRefresh />,
    groupKey: 'model',
    description: '논리-물리 모델 전환 규칙'
  },
  standardInterface: {
    key: 'standardInterface',
    label: '표준연계',
    icon: <HiOutlineLink />,
    groupKey: 'model',
    description: '표준 데이터 연계 설정'
  },
  analysisRuleset: {
    key: 'analysisRuleset',
    label: '분석기준',
    icon: <HiOutlineAdjustments />,
    groupKey: 'model',
    description: '핵심엔터티, 관계 추정 등 분석 기준'
  },
  standardInvestigation: {
    key: 'standardInvestigation',
    label: '표준조사기준',
    icon: <HiOutlineClipboardCheck />,
    groupKey: 'model',
    description: '명칭, 단어, 용어, 도메인 조사 기준'
  },

  // 사용자 설정
  profile: {
    key: 'profile',
    label: '프로파일',
    icon: <HiOutlineUserCircle />,
    groupKey: 'user',
    description: '사용자 정보 수정'
  },
  shareGroups: {
    key: 'shareGroups',
    label: '공유그룹',
    icon: <HiOutlineUserGroup />,
    groupKey: 'user',
    description: '참여 중인 공유그룹 목록'
  },
  statistics: {
    key: 'statistics',
    label: '통계',
    icon: <HiOutlineChartBar />,
    groupKey: 'user',
    description: '사용자 활동 통계'
  },

  // 환경 설정
  diagram: {
    key: 'diagram',
    label: '다이어그램',
    icon: <HiOutlineViewGridAdd />,
    groupKey: 'environment',
    description: 'ERD 다이어그램 표시 설정'
  },
  theme: {
    key: 'theme',
    label: '테마',
    icon: <HiOutlineColorSwatch />,
    groupKey: 'environment',
    description: '테마 및 외관 설정'
  }
};

// ==================== 헬퍼 함수 ====================

/**
 * 메뉴 키로부터 그룹 키를 가져옵니다
 * @param {string} menuKey - 메뉴 키
 * @returns {string} 그룹 키
 */
export const getGroupKeyFromMenu = (menuKey) => {
  return SETTING_MENUS[menuKey]?.groupKey || 'model';
};

/**
 * 메뉴 키로부터 그룹 정보를 가져옵니다
 * @param {string} menuKey - 메뉴 키
 * @returns {SettingGroup} 그룹 정보
 */
export const getGroupFromMenu = (menuKey) => {
  const groupKey = getGroupKeyFromMenu(menuKey);
  return SETTING_GROUPS[groupKey] || SETTING_GROUPS.model;
};

// ==================== 통합 설정 메뉴 (자동 생성) ====================

/**
 * SETTING_MENUS를 기반으로 통합 메뉴 배열을 생성합니다
 * SettingsDrawer에서 사용하는 형식으로 변환
 */
export const unifiedSettingsMenuConfig = Object.values(SETTING_MENUS).map(menu => {
  const group = SETTING_GROUPS[menu.groupKey];
  return {
    key: menu.key,
    label: menu.label,
    icon: menu.icon,
    group: menu.groupKey,
    groupTitle: group?.title || '',
    groupOrder: group?.order || 99,
    description: menu.description
  };
});

// ==================== 개별 그룹별 메뉴 (하위 호환성) ====================

/** 모델 설정 메뉴 */
export const modelSettingsMenuConfig = unifiedSettingsMenuConfig.filter(
  menu => menu.group === 'model'
);

/** 사용자 설정 메뉴 */
export const userSettingsMenuConfig = unifiedSettingsMenuConfig.filter(
  menu => menu.group === 'user'
);

/** 환경 설정 메뉴 */
export const configurationMenuConfig = unifiedSettingsMenuConfig.filter(
  menu => menu.group === 'environment'
);
